import { useEffect } from "react";

const SUFFIX = "astrogenesis";

/**
 * usePageMeta — sets document.title + a few SEO/social meta tags for the
 * current page, and reverts them on unmount so navigation between pages
 * never leaks meta.
 *
 * Everything happens in document.head. The static <meta> tags in index.html
 * are the *default* values for pages that don't call this hook.
 *
 * @param meta.title          e.g. "Cast a Chart"  →  document.title
 * @param meta.description    e.g. "Enter the moment the watch began winding."
 * @param meta.canonicalPath  e.g. "/cast"  →  full URL "https://astrogenesis.net/cast"
 *                                           (omit to use the current location)
 * @param meta.ogImage        absolute URL or path under /og-cards/
 * @param meta.noIndex        set to true to add <meta name="robots" content="noindex">
 */
export interface PageMeta {
  title?: string | null;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  noIndex?: boolean;
  citationMeta?: CitationMeta;
}

export interface CitationMeta {
  title: string;
  author?: string;
  date?: string; // ISO-8601 or free-form
  abstractUrl?: string;
  journalTitle?: string;
  volume?: string;
  issue?: string;
  firstpage?: string;
  lastpage?: string;
  doi?: string;
  pdfUrl?: string;
  publisher?: string;
  keywords?: string[];
  authors?: string[];
}

const BASE_URL = "https://astrogenesis.net";

function setMeta(name: string, attr: "name" | "property", content: string | null | undefined, attrValue: string) {
  if (content == null) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  // Track the previous value so unmount can restore it
  el.dataset["astroPrev" + attrValue] = el.dataset["astroPrev" + attrValue] ?? el.getAttribute("content") ?? "";
}

function setLink(rel: string, href: string | null | undefined, attrValue: string) {
  if (href == null) return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.dataset["astroPrev" + attrValue] = el.dataset["astroPrev" + attrValue] ?? el.getAttribute("href") ?? "";
}

export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const previousTitle = document.title;
    const managedSelector = 'meta[name="description"], meta[name="robots"], meta[property^="og:"], meta[name^="citation_"], link[rel="canonical"]';
    const previous = [...document.head.querySelectorAll<HTMLMetaElement | HTMLLinkElement>(managedSelector)].map(el => ({ el, attributes: [...el.attributes].map(a => [a.name, a.value] as const) }));
    const originalElements = new Set(previous.map(item => item.el));
    // 1. document.title
    if (meta.title != null) {
      document.title = `${meta.title} — ${SUFFIX}`;
    } else {
      document.title = SUFFIX;
    }

    // 2. description
    if (meta.description != null) {
      setMeta("description", "name", meta.description, "Description");
    }

    // 3. canonical
    if (meta.canonicalPath != null) {
      const href = meta.canonicalPath.startsWith("http")
        ? meta.canonicalPath
        : `${BASE_URL}${meta.canonicalPath.startsWith("/") ? meta.canonicalPath : "/" + meta.canonicalPath}`;
      setLink("canonical", href, "Canonical");
    }

    // 4. og:title, og:description, og:url, og:image
    if (meta.title != null)       setMeta("og:title", "property", `${meta.title} — ${SUFFIX}`, "OgTitle");
    if (meta.description != null) setMeta("og:description", "property", meta.description, "OgDescription");
    if (meta.canonicalPath != null) {
      const href = meta.canonicalPath.startsWith("http") ? meta.canonicalPath : `${BASE_URL}${meta.canonicalPath.startsWith("/") ? meta.canonicalPath : "/" + meta.canonicalPath}`;
      setMeta("og:url", "property", href, "OgUrl");
    }
    if (meta.ogImage != null)     setMeta("og:image", "property", meta.ogImage, "OgImage");

    // 5. noindex
    setMeta("robots", "name", meta.noIndex ? "noindex" : "index,follow", "Robots");

    // 6. Google Scholar citation_* meta tags
    if (meta.citationMeta) {
      const cm = meta.citationMeta;
      if (cm.title)       setMeta("citation_title",            "name", cm.title,       "CitationTitle");
      if (cm.date)        setMeta("citation_publication_date", "name", cm.date,        "CitationDate");
      if (cm.doi)         setMeta("citation_doi",                "name", cm.doi,         "CitationDoi");
      if (cm.abstractUrl) setMeta("citation_abstract_html_url", "name", cm.abstractUrl, "CitationAbstractUrl");
      if (cm.pdfUrl)      setMeta("citation_pdf_url",            "name", cm.pdfUrl,      "CitationPdfUrl");
      if (cm.journalTitle)setMeta("citation_journal_title",    "name", cm.journalTitle, "CitationJournalTitle");
      if (cm.volume)      setMeta("citation_volume",             "name", cm.volume,      "CitationVolume");
      if (cm.issue)       setMeta("citation_issue",              "name", cm.issue,       "CitationIssue");
      if (cm.firstpage)   setMeta("citation_firstpage",          "name", cm.firstpage,   "CitationFirstpage");
      if (cm.lastpage)    setMeta("citation_lastpage",           "name", cm.lastpage,    "CitationLastpage");
      if (cm.publisher)   setMeta("citation_publisher",          "name", cm.publisher,   "CitationPublisher");
      if (cm.author) {
        setMeta("citation_author", "name", cm.author, "CitationAuthor");
      }
      if (cm.authors && cm.authors.length) {
        // Remove any previous multi-author tags to avoid stale accumulation.
        document.head.querySelectorAll('meta[name="citation_author"]').forEach((el) => el.remove());
        for (const a of cm.authors) {
          const el = document.createElement("meta");
          el.setAttribute("name", "citation_author");
          el.setAttribute("content", a);
          document.head.appendChild(el);
        }
      }
      if (cm.keywords && cm.keywords.length) {
        setMeta("citation_keywords", "name", cm.keywords.join(", "), "CitationKeywords");
      }
    }

    return () => {
      document.title = previousTitle;
      // Restore receiving head state, including attributes before mutation.
      // Remove only tags created by this hook; never reset to marketing copy.
      document.head.querySelectorAll(managedSelector).forEach(el => {
        if (!originalElements.has(el as HTMLMetaElement | HTMLLinkElement)) el.remove();
      });
      for (const { el, attributes } of previous) {
        for (const attribute of [...el.attributes]) el.removeAttribute(attribute.name);
        for (const [name, value] of attributes) el.setAttribute(name, value);
        if (!el.isConnected) document.head.appendChild(el);
      }
    };
  }, [meta.title, meta.description, meta.canonicalPath, meta.ogImage, meta.noIndex ? "1" : "0", meta.citationMeta ? JSON.stringify(meta.citationMeta) : ""]);
}

/**
 * Backwards-compat shim — pages that only set a title can keep using
 * usePageTitle(...). Internally we just wrap usePageMeta.
 */
export function usePageTitle(title?: string | null) {
  usePageMeta({ title: title ?? undefined });
}
